import { useState, useEffect, useCallback } from 'react';

export interface CookiePreferences {
  essential: boolean; // Always true, cannot be disabled
  analytics: boolean;
  functional: boolean;
  marketing: boolean; // For future use
  timestamp: number;
  version: string;
}

const STORAGE_KEY = 'cookie_preferences';
const PREFERENCES_VERSION = '1.0';

const defaultPreferences: CookiePreferences = {
  essential: true,
  analytics: false,
  functional: false,
  marketing: false,
  timestamp: 0,
  version: PREFERENCES_VERSION,
};

export const useCookieConsent = () => {
  const [preferences, setPreferences] = useState<CookiePreferences | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Load preferences from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as CookiePreferences;
        // Check if preferences are valid and not outdated
        if (parsed.version === PREFERENCES_VERSION && parsed.timestamp > 0) {
          setPreferences(parsed);
          // Initialize scripts based on preferences
          if (parsed.analytics) {
            initAnalytics();
          }
          if (parsed.functional) {
            initFunctional();
          }
        } else {
          // Old version, show banner again
          setShowBanner(true);
        }
      } catch {
        setShowBanner(true);
      }
    } else {
      // No preferences stored, show banner after delay
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const savePreferences = useCallback((newPreferences: Partial<CookiePreferences>) => {
    const updated: CookiePreferences = {
      ...defaultPreferences,
      ...newPreferences,
      essential: true, // Always true
      timestamp: Date.now(),
      version: PREFERENCES_VERSION,
    };
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    setPreferences(updated);
    setShowBanner(false);
    setShowModal(false);

    // Initialize or disable scripts based on new preferences
    if (updated.analytics) {
      initAnalytics();
    } else {
      disableAnalytics();
    }
    
    if (updated.functional) {
      initFunctional();
    }
  }, []);

  const acceptAll = useCallback(() => {
    savePreferences({
      analytics: true,
      functional: true,
      marketing: false, // Not used yet
    });
  }, [savePreferences]);

  const rejectNonEssential = useCallback(() => {
    savePreferences({
      analytics: false,
      functional: false,
      marketing: false,
    });
  }, [savePreferences]);

  const openPreferences = useCallback(() => {
    setShowModal(true);
  }, []);

  const closePreferences = useCallback(() => {
    setShowModal(false);
  }, []);

  const hasConsent = useCallback((type: keyof Omit<CookiePreferences, 'timestamp' | 'version'>) => {
    if (!preferences) return false;
    return preferences[type];
  }, [preferences]);

  return {
    preferences,
    showBanner,
    showModal,
    acceptAll,
    rejectNonEssential,
    savePreferences,
    openPreferences,
    closePreferences,
    hasConsent,
    setShowBanner,
  };
};

// Analytics initialization placeholder
export const initAnalytics = () => {
  // Enable Google Analytics tracking
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: 'granted',
    });
  }
  
  // Add any other analytics initialization here
  if (import.meta.env.DEV) {
    console.log('[Cookie Consent] Analytics initialized');
  }
};

// Disable analytics
export const disableAnalytics = () => {
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: 'denied',
    });
  }
  
  if (import.meta.env.DEV) {
    console.log('[Cookie Consent] Analytics disabled');
  }
};

// Functional cookies initialization placeholder
export const initFunctional = () => {
  // Add functional cookie initialization here
  // e.g., theme preferences, language settings, etc.
  if (import.meta.env.DEV) {
    console.log('[Cookie Consent] Functional cookies initialized');
  }
};

// Marketing cookies initialization placeholder (for future use)
export const initMarketing = () => {
  // Add marketing/advertising cookie initialization here
  // e.g., Facebook Pixel, Google Ads, etc.
  if (import.meta.env.DEV) {
    console.log('[Cookie Consent] Marketing cookies initialized');
  }
};

// Check if user has given consent
export const hasStoredConsent = (): boolean => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return false;
  
  try {
    const parsed = JSON.parse(stored) as CookiePreferences;
    return parsed.version === PREFERENCES_VERSION && parsed.timestamp > 0;
  } catch {
    return false;
  }
};

// Get stored preferences
export const getStoredPreferences = (): CookiePreferences | null => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return null;
  
  try {
    return JSON.parse(stored) as CookiePreferences;
  } catch {
    return null;
  }
};
